import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  ListItemText,
  ListItemAvatar,
  Box,
} from "@material-ui/core";
import {
  CloudUpload,
  Delete,
  InsertDriveFile,
  Image,
  Close,
  Link,
} from "@material-ui/icons";
import moment from "moment";
import { getAddressDetails } from "../AddressBook/actions";
import { getCurrentUserId } from "../../../utils";
import CounterPartySelection from "../../../components/Common/CounterPartySelection";

const CreateCargoContractForm = ({
  isReadOnly = false,
  formData = {},
  setFormData = () => { },
  handleChange = () => { },
  removeAttachment = () => { },
  textContract,
  handleTextContractUpload = () => { },
  handleAttachmentUpload = () => { },
  attachments,
  setTextContract = () => { },
  isEditing = false,
  setPrevAttachments = () => { },
  prevAttachments,
}) => {
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [counterPartyDetails, setCounterPartyDetails] = useState();
  const [organizationAddressBook, setOrganizationAddressBook] = useState([]);

  const textContractInputRef = useRef(null);
  const attachmentInputRef = useRef(null);

  const transformAddressItems = (list = []) => {
    return list.map(item => {
      let fullName = "";

      if (item.type === "Individual") {
        const { individual_firstName = "", individual_middleName = "", individual_lastName = "" } = item;
        fullName = [individual_firstName, individual_middleName, individual_lastName].filter(Boolean).join(" ");
      } else if (item.type === "Organisation") {
        fullName = item.organisation_fullName || "";
      }

      return {
        _id: item._id,
        type: item.type,
        fullName,
      };
    });
  };

  const fetchCounterPartyDetails = async () => {
    const userId = getCurrentUserId();

    try {
      const [userRes, orgRes] = await Promise.all([
        getAddressDetails({ userId, addressBookType: "user", perPage: 100 }),
        getAddressDetails({ userId, addressBookType: "organization", perPage: 100 }),
      ]);

      setCounterPartyDetails(transformAddressItems(userRes?.data?.list));
      setOrganizationAddressBook(transformAddressItems(orgRes?.data?.list));
    } catch (error) {
      console.error("Failed to fetch counter party details:", error);
      setCounterPartyDetails([]);
      setOrganizationAddressBook([]);
    }
  };

  useEffect(() => {
    fetchCounterPartyDetails();
  }, []);

  useEffect(() => {
    if (formData && !formData.cpDate && typeof setFormData === "function") {
      setFormData((prev) => ({
        ...prev,
        cpDate: moment().format("YYYY-MM-DD"),
      }));
    }
  }, [formData?.cpDate]);

  const getFileExtension = (url) => {
    try {
      const pathname = new URL(url).pathname;
      return pathname.substring(pathname.lastIndexOf(".")).toLowerCase();
    } catch {
      return "";
    }
  };

  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
  const videoExtensions = [".mp4", ".mov", ".avi", ".wmv", ".flv", ".mkv"];

  const isImageFile = (url) => {
    const ext = getFileExtension(url);
    return imageExtensions.includes(ext);
  };

  const isVideoFile = (url) => {
    const ext = getFileExtension(url);
    return videoExtensions.includes(ext);
  };

  const allFiles = [
    ...(prevAttachments?.attachments || []),
    ...(prevAttachments?.textContract ? [prevAttachments.textContract] : []),
  ];

  const imageCount = allFiles?.filter((url) => isImageFile(url))?.length;
  const videoCount = allFiles?.filter((url) => isVideoFile(url))?.length;
  const fileCount = allFiles?.filter(
    (url) => !isImageFile(url) && !isVideoFile(url)
  )?.length;

  const updateCounterPartyAtPort = (portId, newCounterParties) => {
    setFormData(prevState => ({
      ...prevState,
      cargoList: prevState.cargoList.map(port => port._id === portId ? { ...port, counterParties: newCounterParties } : port)
    }));
  };

  const handleDemurrageChange = (portId, newValue) => {
    setFormData(prevState => ({
      ...prevState,
      cargoList: prevState.cargoList.map(port => port._id === portId ? { ...port, demurrage: newValue } : port)
    }));
  };

  const handleDespatchChange = (portId, newValue) => {
    setFormData(prevState => ({
      ...prevState,
      cargoList: prevState.cargoList.map(port => port._id === portId ? { ...port, despatch: newValue } : port)
    }));
  };

  return (
    <form className="bg-white space-y-2">
      {/* Top Section Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-start">
        <div className="w-full bg-white border border-[#cbd5e1] rounded-none overflow-hidden shadow-none">
          <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1]">
            <h2 className="text-[11px] font-medium text-slate-800">
              Basic Information
            </h2>
          </div>
          <table className="w-full border-collapse text-left table-fixed">
            <tbody>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white w-[38%] align-middle">
                  Cargo Fixture ID
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    name="cargoFixtureId"
                    value={formData?.cargoFixtureId}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                    placeholder={isReadOnly ? "" : "Manual ID"}
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Estimation ID
                </td>
                <td className="p-0 align-middle bg-slate-100">
                  <input
                    name="estId"
                    value={formData?.estId || "-"}
                    disabled={true}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none text-slate-500 cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Contract Name <span className="text-rose-500">*</span>
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    required
                    name="contractName"
                    value={formData?.contractName}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  CP Date <span className="text-rose-500">*</span>
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    required
                    type="date"
                    name="cpDate"
                    value={
                      formData?.cpDate
                        ? moment(formData?.cpDate).format("YYYY-MM-DD")
                        : moment().format("YYYY-MM-DD")
                    }
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className={`w-fit max-w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border inline-flex items-center text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  CounterParty Style
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    name="counterpartyFullStyle"
                    value={formData?.counterpartyFullStyle}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr>
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-top pt-1">
                  Banking Details
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <textarea
                    name="bankingDetails"
                    value={formData?.bankingDetails}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    rows={2}
                    className="w-full px-1.5 py-0 text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 resize-none font-sans block disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Contract Details - Right Column */}
        <div className="w-full bg-white border border-[#cbd5e1] rounded-none overflow-hidden shadow-none">
          <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1]">
            <h2 className="text-[11px] font-medium text-slate-800">
              Contract Details
            </h2>
          </div>
          <table className="w-full border-collapse text-left table-fixed">
            <tbody>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white w-[38%] align-middle">
                  Contracting Company <span className="text-rose-500">*</span>
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    required
                    name="contractingCompany"
                    value={formData?.contractingCompany}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Department
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    name="department"
                    value={formData?.department}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Trade Area
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    name="tradeArea"
                    value={formData?.tradeArea}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Vessel Type
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    name="vesselType"
                    value={formData?.vesselType}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Min Qty
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    type="number"
                    name="minQty"
                    value={formData?.minQty}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Max Qty
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    type="number"
                    name="maxQty"
                    value={formData?.maxQty}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr className="border-b border-[#cbd5e1]">
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Freight Rate
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    type="number"
                    name="freightRate"
                    value={formData?.freightRate}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
              <tr>
                <td className="border-r border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white align-middle">
                  Currency
                </td>
                <td className={`p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"}`}>
                  <input
                    type="text"
                    name="currency"
                    value={formData?.currency}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipment Details Row */}
      <div className="w-full bg-white border border-[#cbd5e1] rounded-none overflow-hidden shadow-none">
        <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1]">
          <h2 className="text-[11px] font-medium text-slate-800">
            Shipment Details
          </h2>
        </div>
        <table className="block md:table w-full border-collapse text-left table-fixed">
          <tbody className="block md:table-row-group">
            <tr className="grid grid-cols-[40%_60%] md:table-row [&>td:nth-last-child(-n+2)]:border-b-0">
              <td className="border-r border-b md:border-b-0 border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white w-auto md:w-[15%] align-middle">
                Shipment No.
              </td>
              <td className={`border-r border-b md:border-b-0 border-[#cbd5e1] p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"} w-auto md:w-[18%]`}>
                <input
                  name="shipmentNo"
                  value={formData?.shipmentNo}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                />
              </td>
              <td className="border-r border-b md:border-b-0 border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white w-auto md:w-[15%] align-middle">
                Chartering PIC
              </td>
              <td className={`border-r border-b md:border-b-0 border-[#cbd5e1] p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"} w-auto md:w-[18%]`}>
                <input
                  name="charteringPIC"
                  value={formData?.charteringPIC}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                />
              </td>
              <td className="border-r border-b md:border-b-0 border-[#cbd5e1] px-1.5 py-0 text-[11px] font-medium text-slate-900 bg-white w-auto md:w-[15%] align-middle">
                Ops Coordinator
              </td>
              <td className={`border-b md:border-b-0 p-0 align-middle ${isReadOnly ? "bg-slate-100 cursor-not-allowed" : "bg-[#ffffd6]"} w-auto md:w-[19%]`}>
                <input
                  name="opsCoordinator"
                  value={formData?.opsCoordinator}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className="w-full h-full min-h-[18px] px-1.5 py-0 m-0 box-border block text-[11px] font-medium bg-transparent border-none outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900 disabled:cursor-not-allowed"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Port CounterParty Selection (Rendered dynamically if length > 0) */}
      {formData?.cargoList?.length > 0 && (
        <div className="w-full bg-white border border-[#cbd5e1] rounded-none overflow-hidden shadow-none">
          <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1]">
            <h2 className="text-[11px] font-medium text-slate-800">
              Port Counter Party Details
            </h2>
          </div>
          <div className="p-2 bg-slate-50/20">
            <CounterPartySelection
              portList={formData?.cargoList}
              updateCounterPartyAtPort={updateCounterPartyAtPort}
              handleDemurrageChange={handleDemurrageChange}
              handleDespatchChange={handleDespatchChange}
              isMultiSelect={true}
              isReadOnly={isReadOnly}
            />
          </div>
        </div>
      )}

      {/* Attachments & Comments Section */}
      <div className="border border-[#cbd5e1] rounded-none bg-white overflow-hidden shadow-none">
        <div className="bg-[#e2e8f0] px-2 py-1 border-b border-[#cbd5e1]">
          <h2 className="text-[11px] font-medium text-slate-800">
            {isEditing ? "Attachments & Comments" : "Files & Comments"}
          </h2>
        </div>

        <div className="p-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - File Uploads */}
          <div className="space-y-3">
            {isEditing && (
              <div
                onClick={() => setIsAttachmentModalOpen(true)}
                className="inline-flex items-center border border-indigo text-indigo text-[11px] font-medium px-2.5 py-0.5 rounded-full cursor-pointer hover:bg-indigo-50 transition"
              >
                <Link fontSize="small" className="mr-1" />
                Media: {imageCount} Images, {videoCount} Videos, {fileCount} Files
              </div>
            )}

            <div className="space-y-1.5 flex flex-col">
              {/* Text Contract Row */}
              <div className="flex items-center gap-2">
                <div className="w-1/3">
                  <span className="text-[11px] font-medium text-slate-700 block">
                    Text Contract
                  </span>
                  <span className="text-[11px] font-medium text-slate-500 leading-tight block">
                    PDF, DOC (Max 10MB)
                  </span>
                </div>
                <div className="w-2/3 flex items-center flex-wrap gap-1.5">
                  <input
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="text-contract-upload"
                    type="file"
                    ref={textContractInputRef}
                    disabled={isReadOnly}
                    onChange={handleTextContractUpload}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CloudUpload className="text-white" style={{ fontSize: '12px' }} />}
                    disabled={isReadOnly}
                    onClick={() => textContractInputRef.current?.click()}
                    className="!w-[115px] !min-w-[115px] !max-w-[115px]"
                    style={{ textTransform: 'none', padding: '1px 8px', fontSize: '11px', fontWeight: '500', height: '22px', backgroundColor: isReadOnly ? '#94a3b8' : '#1e293b', color: '#fff', borderRadius: '0px', width: '115px', minWidth: '115px', maxWidth: '115px', justifyContent: 'center', whiteSpace: 'nowrap' }}
                  >
                    Upload
                  </Button>
                  {textContract && (
                    <div className="flex items-center bg-[#ffffd6] border border-[#cbd5e1] rounded-none px-1.5 py-0.5 max-w-[180px]">
                      <InsertDriveFile style={{ fontSize: '12px' }} className="text-gray-500 mr-1" />
                      <span className="text-[11px] font-medium text-slate-800 truncate max-w-[100px] mr-1">
                        {textContract.name}
                      </span>
                      {!isReadOnly && (
                        <IconButton
                          size="small"
                          onClick={() => setTextContract(null)}
                          className="text-rose-500 hover:text-rose-700 p-0.5"
                        >
                          <Delete style={{ fontSize: '12px' }} />
                        </IconButton>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Attachments Row */}
              <div className="flex items-start gap-2">
                <div className="w-1/3">
                  <span className="text-[11px] font-medium text-slate-700 block">
                    Attachments
                  </span>
                  <span className="text-[11px] font-medium text-slate-500 leading-tight block">
                    PDF, XLS, JPG, PNG (Max 5MB)
                  </span>
                </div>
                <div className="w-2/3 flex flex-col items-start gap-1">
                  <input
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    className="hidden"
                    id="attachment-upload"
                    multiple
                    type="file"
                    ref={attachmentInputRef}
                    disabled={isReadOnly}
                    onChange={handleAttachmentUpload}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload style={{ fontSize: '12px' }} />}
                    disabled={isReadOnly}
                    onClick={() => attachmentInputRef.current?.click()}
                    className="!w-[115px] !min-w-[115px] !max-w-[115px]"
                    style={{ textTransform: 'none', padding: '1px 8px', fontSize: '11px', fontWeight: '500', height: '22px', borderRadius: '0px', borderColor: '#cbd5e1', width: '115px', minWidth: '115px', maxWidth: '115px', justifyContent: 'center', whiteSpace: 'nowrap' }}
                  >
                    Upload More
                  </Button>

                  {attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {attachments.map((file, index) => (
                        <Chip
                          key={index}
                          size="small"
                          icon={
                            file.type.includes("image") ? (
                              <Image style={{ fontSize: '12px', marginLeft: '2px' }} />
                            ) : (
                              <InsertDriveFile style={{ fontSize: '12px', marginLeft: '2px' }} />
                            )
                          }
                          label={
                            <Tooltip title={`${file.name} - ${(file.size / 1024).toFixed(2)} KB`}>
                              <span className="truncate max-w-[90px] text-[11px] font-medium">
                                {file.name}
                              </span>
                            </Tooltip>
                          }
                          onDelete={isReadOnly ? undefined : () => removeAttachment(index)}
                          deleteIcon={
                            !isReadOnly ? (
                              <Delete
                                style={{ fontSize: '12px' }}
                                className="text-rose-500 hover:text-rose-700"
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            ) : undefined
                          }
                          className="bg-[#ffffd6] border border-[#cbd5e1] rounded-none"
                          variant="outlined"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Comments */}
          <div className="flex flex-col">
            <span className="text-[11px] font-medium text-slate-700 mb-0.5">
              Comments
            </span>
            <textarea
              name="comments"
              value={formData?.comments || ""}
              onChange={handleChange}
              rows={3}
              disabled={isReadOnly}
              readOnly={isReadOnly}
              placeholder={isReadOnly ? "" : "Enter your comments here..."}
              className={`w-full px-1.5 py-1 text-[11px] font-medium border border-[#cbd5e1] rounded-none outline-none resize-none font-sans ${
                isReadOnly
                  ? "bg-slate-100 text-slate-600 cursor-not-allowed"
                  : "bg-[#ffffd6] focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 text-slate-900"
              }`}
            />
          </div>
        </div>
      </div>
        {/* Attachments Modal for Edit Mode */}
        <Dialog
          open={isAttachmentModalOpen}
          onClose={() => setIsAttachmentModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ padding: '12px 16px' }}>
            <div className="flex justify-between items-center text-sm font-semibold">
              <span>Attachments</span>
              <IconButton size="small" onClick={() => setIsAttachmentModalOpen(false)}>
                <Close fontSize="small" />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers style={{ padding: '8px 16px' }}>
            {prevAttachments?.attachments?.length > 0 || prevAttachments?.textContract ? (
              <List disablePadding>
                {/* Text Contract */}
                {prevAttachments?.textContract && (
                  <Box>
                    <ListItem alignItems="center" style={{ padding: '4px 0' }}>
                      <ListItemAvatar style={{ minWidth: '40px' }}>
                        <Avatar
                          variant="rounded"
                          className="bg-gray-100 text-gray-600"
                          style={{ width: '30px', height: '30px' }}
                        >
                          <InsertDriveFile fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <a
                            href={prevAttachments.textContract}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo underline text-[11px]"
                          >
                            Open Contract File
                          </a>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary" className="text-[10px] truncate max-w-[200px] block">
                            {prevAttachments.textContract.split("/").pop()}
                          </Typography>
                        }
                        style={{ margin: 0 }}
                      />

                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setPrevAttachments((prev) => ({ ...prev, textContract: null }))}
                        className="text-rose-500 hover:text-rose-700"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItem>
                    {prevAttachments?.attachments?.length > 0 && <Divider className="my-1" />}
                  </Box>
                )}

                {/* Additional Attachments */}
                {prevAttachments?.attachments?.map((url, index) => {
                  const isImage = isImageFile(url);
                  return (
                    <Box key={index}>
                      <ListItem alignItems="center" style={{ padding: '4px 0' }}>
                        <ListItemAvatar style={{ minWidth: '40px' }}>
                          {isImage ? (
                            <Avatar
                              variant="rounded"
                              src={url}
                              alt={`Attachment ${index}`}
                              className="cursor-pointer"
                              style={{ width: '30px', height: '30px' }}
                              onClick={() => window.open(url, "_blank")}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              className="bg-gray-100 text-gray-600"
                              style={{ width: '30px', height: '30px' }}
                            >
                              <InsertDriveFile fontSize="small" />
                            </Avatar>
                          )}
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            isImage ? (
                              <Typography
                                variant="body2"
                                className="text-indigo underline cursor-pointer text-[11px]"
                                onClick={() => window.open(url, "_blank")}
                              >
                                Open Image
                              </Typography>
                            ) : (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo underline text-[11px]"
                              >
                                Open File
                              </a>
                            )
                          }
                          secondary={
                            <Typography variant="caption" color="textSecondary" className="text-[10px] truncate max-w-[200px] block">
                              {url.split("/").pop()}
                            </Typography>
                          }
                          style={{ margin: 0 }}
                        />

                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => {
                            const updated = [...prevAttachments.attachments];
                            updated.splice(index, 1);
                            setPrevAttachments((prev) => ({ ...prev, attachments: updated }));
                          }}
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItem>
                      {index < prevAttachments.attachments.length - 1 && <Divider className="my-1" />}
                    </Box>
                  );
                })}
              </List>
            ) : (
              <Typography variant="caption" color="textSecondary" align="center" className="py-4 block">
                No attachments found.
              </Typography>
            )}
          </DialogContent>
          <DialogActions style={{ padding: '8px 16px' }}>
            <Button
              size="small"
              onClick={() => setIsAttachmentModalOpen(false)}
              color="primary"
              style={{ textTransform: 'none' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
    </form>
  );
};

export default CreateCargoContractForm;